// Mirrors findingsSyntheticArgsSchema in packages/middleware/src/findings/
// findings-synthetic.runner.ts — keep the shape in sync: { mode?, dryRun?, scope?, confirm? }
// where mode is 'generate' (default) or 'wipe'; wipe needs scope 'synthetic' | 'all', and
// 'all' needs confirm = WIPE_ALL_CONFIRMATION. dryRun absent = dry run.
export type SyntheticAction = 'generate' | 'wipe-synthetic' | 'wipe-all'

export const WIPE_ALL_CONFIRMATION = 'wipe-all-findings'

function field(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null && key in value
    ? (value as Record<string, unknown>)[key]
    : undefined
}

export function syntheticAction(value: unknown): SyntheticAction {
  if (field(value, 'mode') !== 'wipe') return 'generate'
  return field(value, 'scope') === 'all' ? 'wipe-all' : 'wipe-synthetic'
}

export function isDryRun(value: unknown): boolean {
  return field(value, 'dryRun') !== false
}

export function extractConfirm(value: unknown): string {
  const confirm = field(value, 'confirm')
  return typeof confirm === 'string' ? confirm : ''
}

export function buildFindingsSyntheticArgs(
  action: SyntheticAction,
  dryRun: boolean,
  confirm: string,
) {
  const write = dryRun ? {} : { dryRun: false }
  if (action === 'generate') return { mode: 'generate', ...write }
  if (action === 'wipe-synthetic') return { mode: 'wipe', scope: 'synthetic', ...write }
  return { mode: 'wipe', scope: 'all', confirm, ...write }
}

export function validateFindingsSyntheticArgs(value: unknown): string | null {
  if (value !== undefined && (typeof value !== 'object' || value === null)) {
    return 'Args must be an object'
  }
  const mode = field(value, 'mode')
  if (mode !== undefined && mode !== 'generate' && mode !== 'wipe') {
    return "Mode must be 'generate' or 'wipe'"
  }
  const dryRun = field(value, 'dryRun')
  if (dryRun !== undefined && typeof dryRun !== 'boolean') return 'Dry run must be true or false'
  const scope = field(value, 'scope')
  if (mode !== 'wipe') {
    return scope !== undefined || field(value, 'confirm') !== undefined
      ? 'Scope and confirmation apply to a wipe only'
      : null
  }
  if (scope !== 'synthetic' && scope !== 'all') return "Pick what to wipe: 'synthetic' or 'all'"
  if (scope === 'all' && extractConfirm(value) !== WIPE_ALL_CONFIRMATION) {
    return `Type ${WIPE_ALL_CONFIRMATION} to wipe every findings row`
  }
  return null
}
